import React, { useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useCart } from '../Context/CartContext'
import { useAuth } from '../Context/AuthContext'
import { useCurrency } from '../Context/CurrencyContext'
import useTemplateScripts from '../hooks/useTemplateScripts'
import { getAllProducts } from '../services/productService'
import { submitContactForm } from '../services/contactService'
import { CATEGORY_GROUPS, classifyCategory, collectionPath, isGeneralGroup } from '../constants/shopCategories'
import { CONTACT_INFO, SITE_LINKS } from '../constants/siteLinks'
import { SERVICE_PAGES, findServicePage, servicePath } from '../constants/servicePages'
import MainHeader from './MainHeader'
import HomeFooter from './HomeFooter'
import CartDrawer from './CartDrawer'
import ProductCard from './ProductCard'
import ProductRail from './ProductRail'
import { Marquee, RotatingWords } from './Motion'
import { SceneBackdrop, SceneOverlay, SceneWidget } from './ServiceScene'
import StepTracker from './StepTracker'
import './ServicePage.css'

const WHATSAPP_NUMBER = CONTACT_INFO.phoneHref.replace(/\D/g, '')
const whatsappLink = (message) => `https://wa.me/${WHATSAPP_NUMBER}${message ? `?text=${encodeURIComponent(message)}` : ''}`

const toCardProduct = (product) => {
  const price = Number(product.priceNew ?? product.price) || 0
  const priceOld = Number(product.priceOld) || null
  return {
    id: product.id || product._id,
    name: product.title || product.name || 'Product',
    title: product.title || product.name || 'Product',
    category: product.category || '',
    price,
    priceOld: priceOld && priceOld > price ? priceOld : null,
    image: product.image || '/placeholder-image.png',
    stock: product.stock ?? null,
  }
}

const scrollToEnquiry = () => document.getElementById('svp-enquire')?.scrollIntoView({ behavior: 'smooth', block: 'start' })

/** Section heading: chip, title with a gradient accent, intro. */
function Head({ chip, title, accent, intro, center = false }) {
  return (
    <header className={`svp-head${center ? ' is-center' : ''}`} data-reveal>
      {chip && <span className="svp-chip">{chip}</span>}
      <h2>
        {title} {accent && <span className="svp-accent">{accent}</span>}
      </h2>
      {intro && <p>{intro}</p>}
    </header>
  )
}

/** Starlink: kit comparison, with live prices from the shop when the kit is listed. */
function KitsFeature({ feature, products, formatPrice }) {
  return (
    <div className="svp-kits">
      {feature.kits.map((kit, index) => {
        const product = products.find((item) => kit.match.test(item.name))
        return (
          <article key={kit.name} className={`svp-kit${kit.tag === 'Most popular' ? ' is-featured' : ''}`} data-reveal={index ? 'right' : 'left'} data-tilt="4">
            <span className="svp-kit__tag">{kit.tag}</span>
            <div className="svp-kit__media">
              <span className="svp-kit__glow" aria-hidden="true" />
              {product ? <img src={product.image} alt={kit.name} loading="lazy" className="fx-float" /> : <i className="fa-solid fa-satellite-dish fx-float" aria-hidden="true" />}
            </div>
            <h3>{kit.name}</h3>
            <p className="svp-kit__best">
              <i className="fa-solid fa-bullseye" aria-hidden="true" />
              Best for: {kit.bestFor}
            </p>
            <ul className="svp-checks">
              {kit.points.map((point) => (
                <li key={point}>
                  <i className="fa-solid fa-check" aria-hidden="true" />
                  {point}
                </li>
              ))}
            </ul>
            <div className="svp-kit__foot">
              {product && (
                <span className="svp-kit__price">
                  <small>Kit price</small>
                  {formatPrice(product.price)}
                </span>
              )}
              <Link
                to={product ? `/product-details/${product.id}` : collectionPath('starlink')}
                state={product ? { product } : undefined}
                className="svp-btn svp-btn--primary"
              >
                {product ? 'View kit' : 'Shop kits'}
                <i className="fa-solid fa-arrow-right" aria-hidden="true" />
              </Link>
            </div>
          </article>
        )
      })}
      {feature.note && (
        <p className="svp-kits__note" data-reveal>
          <i className="fa-solid fa-circle-info" aria-hidden="true" />
          {feature.note}
          <button type="button" onClick={scrollToEnquiry}>
            Ask our team
          </button>
        </p>
      )}
    </div>
  )
}

/** Digital Security: shop sub-category tiles plus ready-made packages. */
function CategoriesFeature({ feature, products }) {
  const subs = (CATEGORY_GROUPS.find((group) => group.id === feature.shopCategory)?.subcategories || []).filter(
    (sub) => !isGeneralGroup(sub)
  )
  const counts = useMemo(() => {
    const result = {}
    products.forEach((product) => {
      const { subcategoryId } = classifyCategory(product.category, product.name)
      result[subcategoryId] = (result[subcategoryId] || 0) + 1
    })
    return result
  }, [products])

  return (
    <>
      <ul className="svp-types">
        {subs.map((sub, index) => (
          <li key={sub.id} data-reveal="zoom" style={{ '--i': index % 4 }}>
            <Link to={collectionPath(sub.id)} className="svp-type">
              <span className="svp-type__icon" aria-hidden="true">
                <i className={`fa-solid ${sub.icon || 'fa-shield-halved'}`} />
              </span>
              <span className="svp-type__name">{sub.name}</span>
              <span className="svp-type__count">{counts[sub.id] ? `${counts[sub.id]} products` : 'On request'}</span>
            </Link>
          </li>
        ))}
      </ul>

      <div className="svp-packages">
        <h3 className="svp-subtitle" data-reveal>
          Popular setups
        </h3>
        <div className="svp-packages__grid">
          {feature.packages.map((pack, index) => (
            <article key={pack.title} className={`svp-package${pack.featured ? ' is-featured' : ''}`} data-reveal style={{ '--i': index }} data-tilt="4">
              {pack.featured && <span className="svp-package__tag">Most requested</span>}
              <span className="svp-package__icon" aria-hidden="true">
                <i className={`fa-solid ${pack.icon}`} />
              </span>
              <h4>{pack.title}</h4>
              <p>{pack.text}</p>
              <ul className="svp-checks">
                {pack.points.map((point) => (
                  <li key={point}>
                    <i className="fa-solid fa-check" aria-hidden="true" />
                    {point}
                  </li>
                ))}
              </ul>
              <button type="button" className={`svp-btn ${pack.featured ? 'svp-btn--primary fx-shine' : 'svp-btn--outline'}`} onClick={scrollToEnquiry}>
                Request a site survey
              </button>
            </article>
          ))}
        </div>
      </div>
    </>
  )
}

/** Network: who we serve (photo grid) and what every delivery includes. */
function AudiencesFeature({ feature }) {
  return (
    <div className="svp-audiences">
      <ul className="svp-audiences__grid">
        {feature.audiences.map((item, index) => (
          <li key={item.label} className="svp-audience" data-reveal="zoom" style={{ '--i': index % 3 }} data-tilt="6">
            <img src={item.image} alt="" loading="lazy" />
            <span>{item.label}</span>
          </li>
        ))}
      </ul>
      <aside className="svp-includes" data-reveal="right">
        <h3>{feature.includesTitle}</h3>
        <ul className="svp-checks">
          {feature.includes.map((item) => (
            <li key={item}>
              <i className="fa-solid fa-check" aria-hidden="true" />
              {item}
            </li>
          ))}
        </ul>
        <button type="button" className="svp-btn svp-btn--primary" onClick={scrollToEnquiry}>
          Request a project quote
          <i className="fa-solid fa-arrow-right" aria-hidden="true" />
        </button>
      </aside>
    </div>
  )
}

/** Canal+ & DStv: one-tap WhatsApp requests. */
function QuickActionsFeature({ feature }) {
  return (
    <ul className="svp-actions">
      {feature.actions.map((action, index) => (
        <li key={action.title} data-reveal style={{ '--i': index }}>
          <a href={whatsappLink(action.message)} target="_blank" rel="noreferrer" className="svp-action" data-tilt="5">
            <span className="svp-action__icon" aria-hidden="true">
              <i className={`fa-solid ${action.icon}`} />
            </span>
            <span className="svp-action__body">
              <strong>{action.title}</strong>
              <span>{action.text}</span>
            </span>
            <span className="svp-action__go">
              <i className="fa-brands fa-whatsapp" aria-hidden="true" />
              WhatsApp
            </span>
          </a>
        </li>
      ))}
    </ul>
  )
}

/** Software: a product showcase and the technologies used. */
function ShowcaseFeature({ feature }) {
  return (
    <div className="svp-showcase">
      <div className="svp-showcase__media" data-reveal="left" data-tilt="4">
        <img src={feature.image} alt={feature.imageAlt || ""} loading="lazy" />
      </div>
      <div className="svp-showcase__body" data-reveal="right">
        <ul className="svp-showcase__points">
          {feature.points.map((point) => (
            <li key={point.title}>
              <span className="svp-icon-chip" aria-hidden="true">
                <i className={`fa-solid ${point.icon}`} />
              </span>
              <span>
                <strong>{point.title}</strong>
                {point.text}
              </span>
            </li>
          ))}
        </ul>
        <h3 className="svp-subtitle">{feature.techTitle}</h3>
        <ul className="svp-tech">
          {feature.tech.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    </div>
  )
}

/** Training: course cards and what every participant gets. */
function CoursesFeature({ feature }) {
  return (
    <>
      <ul className="svp-courses">
        {feature.courses.map((course, index) => (
          <li key={course.title} className="svp-course" data-reveal style={{ '--i': index % 3 }} data-tilt="5">
            <span className="svp-course__icon" aria-hidden="true">
              <i className={`fa-solid ${course.icon}`} />
            </span>
            <h3>{course.title}</h3>
            <p>{course.text}</p>
            <div className="svp-course__foot">
              <span className="svp-course__level">
                <i className="fa-solid fa-signal" aria-hidden="true" />
                {course.level}
              </span>
              <button type="button" onClick={scrollToEnquiry}>
                Apply
                <i className="fa-solid fa-arrow-right" aria-hidden="true" />
              </button>
            </div>
          </li>
        ))}
      </ul>
      <ul className="svp-perks" data-reveal>
        {feature.perks.map((perk) => (
          <li key={perk}>
            <i className="fa-solid fa-circle-check" aria-hidden="true" />
            {perk}
          </li>
        ))}
      </ul>
    </>
  )
}

/** Enquiry form: sent to the contact API with the page's service, so the team sees what it is about. */
function EnquiryForm({ page }) {
  const { user } = useAuth()
  const isTraining = page.slug === 'training-center'
  const [form, setForm] = useState({ fullName: user?.name || '', email: user?.email || '', phone: '', message: '', privacyAgreed: false })
  const [status, setStatus] = useState({ state: 'idle', message: '' })

  const update = (field) => (e) => setForm((current) => ({ ...current, [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }))

  const submit = async (e) => {
    e.preventDefault()
    if (!form.privacyAgreed) {
      setStatus({ state: 'error', message: 'Please agree to the privacy policy so we can contact you.' })
      return
    }
    setStatus({ state: 'sending', message: '' })
    try {
      await submitContactForm({
        ...form,
        subject: `${isTraining ? 'Application' : 'Enquiry'}: ${page.name}`,
        serviceInterest: page.contactInterest,
      })
      setStatus({ state: 'sent', message: '' })
      setForm((current) => ({ ...current, phone: '', message: '', privacyAgreed: false }))
    } catch (err) {
      setStatus({ state: 'error', message: err.message || 'We could not send your message. Please try again or call us.' })
    }
  }

  if (status.state === 'sent') {
    return (
      <div className="svp-form svp-form--sent" role="status">
        <span className="svp-form__done" aria-hidden="true">
          <i className="fa-solid fa-check" />
        </span>
        <h3>{isTraining ? 'Application received' : 'Message sent'}</h3>
        <p>Thank you! Our team will contact you within one working day. For anything urgent, call {CONTACT_INFO.phone}.</p>
        <button type="button" className="svp-btn svp-btn--outline" onClick={() => setStatus({ state: 'idle', message: '' })}>
          Send another message
        </button>
      </div>
    )
  }

  return (
    <form className="svp-form" onSubmit={submit} noValidate={false}>
      <h3>{isTraining ? 'Apply for a programme' : `Request a quote — ${page.name}`}</h3>
      <div className="svp-form__row">
        <label>
          <span>Full name</span>
          <input type="text" value={form.fullName} onChange={update('fullName')} required autoComplete="name" placeholder="Your full name" />
        </label>
        <label>
          <span>Phone</span>
          <input type="tel" value={form.phone} onChange={update('phone')} autoComplete="tel" placeholder="07X XXX XXXX" />
        </label>
      </div>
      <label>
        <span>Email</span>
        <input type="email" value={form.email} onChange={update('email')} required autoComplete="email" placeholder="you@example.com" />
      </label>
      <label>
        <span>{isTraining ? 'About you' : 'How can we help?'}</span>
        <textarea
          rows={4}
          value={form.message}
          onChange={update('message')}
          required
          placeholder={
            isTraining
              ? 'Your school or background, the programme or course you are interested in, and your preferred start date.'
              : 'Tell us about your site, what you need and your timeline.'
          }
        />
      </label>
      <label className="svp-form__check">
        <input type="checkbox" checked={form.privacyAgreed} onChange={update('privacyAgreed')} />
        <span>I agree that Fabritech may contact me about this request.</span>
      </label>
      {status.state === 'error' && (
        <p className="svp-form__error" role="alert">
          <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
          {status.message}
        </p>
      )}
      <button type="submit" className="svp-btn svp-btn--primary svp-btn--block fx-shine" disabled={status.state === 'sending'}>
        {status.state === 'sending' ? 'Sending…' : isTraining ? 'Send application' : 'Send request'}
        {status.state !== 'sending' && <i className="fa-solid fa-paper-plane" aria-hidden="true" />}
      </button>
    </form>
  )
}

function ServicePage() {
  useTemplateScripts()
  const { slug } = useParams()
  const page = findServicePage(slug)
  const { cartItems } = useCart()
  const { formatPrice } = useCurrency()
  const [showCart, setShowCart] = useState(false)
  const [products, setProducts] = useState([])
  const [openFaq, setOpenFaq] = useState(0)
  const [addedId, setAddedId] = useState(null)

  const productCategory = page?.shopCategory || page?.feature?.shopCategory

  useEffect(() => {
    window.scrollTo({ top: 0 })
    setOpenFaq(0)
  }, [slug])

  useEffect(() => {
    if (!productCategory) {
      setProducts([])
      return undefined
    }
    let cancelled = false
    getAllProducts()
      .then((items) => {
        if (cancelled) return
        const list = (Array.isArray(items) ? items : []).map(toCardProduct)
        setProducts(list.filter((item) => classifyCategory(item.category, item.name).categoryId === productCategory))
      })
      .catch(() => {
        if (!cancelled) setProducts([])
      })
    return () => {
      cancelled = true
    }
  }, [productCategory])

  useEffect(() => {
    if (page) document.title = `${page.name} | Fabritech`
  }, [page])

  // Old ids (e.g. /services/starlink-installation) move to the page's current address
  if (page && page.slug !== slug) return <Navigate to={servicePath(page.slug)} replace />
  if (!page) return <Navigate to="/#services" replace />

  const { hero, feature } = page
  const others = SERVICE_PAGES.filter((item) => item.slug !== page.slug)
  const shopLink = page.shopCategory ? collectionPath(page.shopCategory) : null

  const primaryAction = hero.primary.shopCategory ? (
    <Link to={collectionPath(hero.primary.shopCategory)} className="svp-btn svp-btn--primary svp-btn--lg fx-shine">
      {hero.primary.label}
      <i className="fa-solid fa-arrow-right" aria-hidden="true" />
    </Link>
  ) : (
    <button type="button" className="svp-btn svp-btn--primary svp-btn--lg fx-shine" onClick={scrollToEnquiry}>
      {hero.primary.label}
      <i className="fa-solid fa-arrow-right" aria-hidden="true" />
    </button>
  )

  return (
    <div className="svp-page">
      <MainHeader cartCount={cartItems.length} onCartClick={() => setShowCart((open) => !open)} />

      <main>
        {/* ---------- Banner ---------- */}
        <section className="svp-hero">
          <SceneBackdrop type={page.slug} />
          <div className="svp-container svp-hero__inner">
            <div className="svp-hero__copy">
              <nav className="svp-crumbs" aria-label="Breadcrumb" data-reveal="fade">
                <Link to={SITE_LINKS.home}>Home</Link>
                <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                <a href="/#services">Services</a>
                <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                <span aria-current="page">{page.name}</span>
              </nav>
              <span className="svp-chip" data-reveal style={{ '--i': 1 }}>
                <i className={`fa-solid ${page.icon}`} aria-hidden="true" />
                {hero.eyebrow}
              </span>
              <h1 data-reveal style={{ '--i': 2 }}>
                {hero.title} <span className="fx-gradient-text">{hero.accent}</span>
              </h1>
              {hero.rotate && (
                <p className="svp-hero__for" data-reveal style={{ '--i': 3 }}>
                  <span>Ideal for</span>
                  <RotatingWords words={hero.rotate} className="svp-hero__word" />
                </p>
              )}
              <p className="svp-hero__text" data-reveal style={{ '--i': 4 }}>
                {hero.text}
              </p>
              <div className="svp-hero__actions" data-reveal style={{ '--i': 5 }}>
                {primaryAction}
                <a href={whatsappLink(`Hello Fabritech, I would like to know more about ${page.name}.`)} target="_blank" rel="noreferrer" className="svp-btn svp-btn--outline svp-btn--lg">
                  <i className="fa-brands fa-whatsapp" aria-hidden="true" />
                  Chat on WhatsApp
                </a>
              </div>
              <a href={CONTACT_INFO.phoneHref} className="svp-hero__call" data-reveal style={{ '--i': 6 }}>
                <i className="fa-solid fa-phone" aria-hidden="true" />
                Or call us: <strong>{CONTACT_INFO.phone}</strong>
              </a>
            </div>
            <div className="svp-hero__media" data-reveal="zoom" style={{ '--i': 2 }}>
              <div className="svp-hero__frame" data-tilt="5">
                <img src={hero.image} alt="" />
                <SceneOverlay type={page.slug} />
              </div>
              <SceneWidget type={page.slug} />
              <div className="svp-hero__badge fx-float">
                <span aria-hidden="true">
                  <i className={`fa-solid ${hero.badge.icon}`} />
                </span>
                <div>
                  <strong>{hero.badge.title}</strong>
                  <small>{hero.badge.text}</small>
                </div>
              </div>
            </div>
          </div>

          <div className="svp-container">
            <ul className="svp-facts">
              {page.facts.map((fact, index) => (
                <li key={fact.title} data-reveal style={{ '--i': index + 3 }}>
                  <span className="svp-icon-chip" aria-hidden="true">
                    <i className={`fa-solid ${fact.icon}`} />
                  </span>
                  <span>
                    <strong>{fact.title}</strong>
                    <small>{fact.text}</small>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ---------- Moving ticker of what we do ---------- */}
        <div className="svp-ticker">
          <Marquee items={[...page.offers.items.map((item) => item.title), ...page.facts.map((fact) => fact.title)]} icon={page.icon} speed={45} />
        </div>

        {/* ---------- What we offer ---------- */}
        <section className="svp-section">
          <div className="svp-container">
            <Head chip={page.offers.chip} title={page.offers.title} accent={page.offers.accent} intro={page.offers.intro} />
            <ul className={`svp-offers${page.offers.items.some((item) => item.image) ? '' : ' is-plain'} is-${page.offers.items.length}`}>
              {page.offers.items.map((item, index) => (
                <li key={item.title} className="svp-offer" data-reveal style={{ '--i': index % 4 }} data-tilt="5">
                  {item.image && (
                    <div className="svp-offer__media">
                      <img src={item.image} alt="" loading="lazy" />
                    </div>
                  )}
                  <div className="svp-offer__body">
                    <span className="svp-icon-chip" aria-hidden="true">
                      <i className={`fa-solid ${item.icon}`} />
                    </span>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                    {item.points && (
                      <ul className="svp-tags">
                        {item.points.map((point) => (
                          <li key={point}>{point}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ---------- Section specific to this service ---------- */}
        <section className="svp-section svp-section--tint">
          <div className="svp-container">
            <Head chip={feature.chip} title={feature.title} accent={feature.accent} intro={feature.intro} />
            {feature.type === 'kits' && <KitsFeature feature={feature} products={products} formatPrice={formatPrice} />}
            {feature.type === 'categories' && <CategoriesFeature feature={feature} products={products} />}
            {feature.type === 'audiences' && <AudiencesFeature feature={feature} />}
            {feature.type === 'quick-actions' && <QuickActionsFeature feature={feature} />}
            {feature.type === 'showcase' && <ShowcaseFeature feature={feature} />}
            {feature.type === 'courses' && <CoursesFeature feature={feature} />}
          </div>
        </section>

        {/* ---------- How it works ---------- */}
        <section className="svp-section">
          <div className="svp-container">
            <Head chip="How it works" title="Simple from" accent="start to finish" intro="Follow a real Fabritech job from the first call to the finished result." center />
            <div data-reveal>
              <StepTracker steps={page.steps} tracker={page.tracker} />
            </div>
          </div>
        </section>

        {/* ---------- Related products ---------- */}
        {page.shopCategory && products.length > 0 && (
          <section className="svp-section svp-section--tint svp-section--products">
            <div className="svp-container" data-reveal>
              <ProductRail
                id={`svp-${page.slug}-products`}
                className="svp-rail"
                icon="fa-bag-shopping"
                eyebrow="From our shop"
                title={page.productsTitle}
                subtitle={page.productsIntro || 'Genuine products with warranty — buy online or have them installed.'}
                actions={
                  <Link to={shopLink} className="svp-rail__link">
                    View all
                    <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                  </Link>
                }
              >
                {products.slice(0, 12).map((product) => (
                  <li key={product.id}>
                    <ProductCard
                      product={product}
                      addedProduct={addedId}
                      onAdded={(item) => {
                        setAddedId(item.id)
                        setTimeout(() => setAddedId(null), 1800)
                      }}
                    />
                  </li>
                ))}
              </ProductRail>
            </div>
          </section>
        )}

        {/* ---------- FAQ ---------- */}
        <section className="svp-section">
          <div className="svp-container svp-faq">
            <div className="svp-faq__intro" data-reveal="left">
              <Head chip="Questions" title="Good questions," accent="clear answers" />
              <div className="svp-faq__help" data-tilt="4">
                <span className="svp-icon-chip" aria-hidden="true">
                  <i className="fa-solid fa-headset" />
                </span>
                <div>
                  <strong>Still have a question?</strong>
                  <p>Our team is happy to help — call or message us.</p>
                  <div className="svp-faq__links">
                    <a href={CONTACT_INFO.phoneHref}>
                      <i className="fa-solid fa-phone" aria-hidden="true" />
                      {CONTACT_INFO.phone}
                    </a>
                    <a href={whatsappLink()} target="_blank" rel="noreferrer">
                      <i className="fa-brands fa-whatsapp" aria-hidden="true" />
                      WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <ul className="svp-faq__list">
              {page.faqs.map((faq, index) => {
                const open = openFaq === index
                return (
                  <li key={faq.q} className={open ? 'is-open' : undefined} data-reveal style={{ '--i': index }}>
                    <button type="button" aria-expanded={open} onClick={() => setOpenFaq(open ? -1 : index)}>
                      {faq.q}
                      <i className={`fa-solid ${open ? 'fa-minus' : 'fa-plus'}`} aria-hidden="true" />
                    </button>
                    {open && <p>{faq.a}</p>}
                  </li>
                )
              })}
            </ul>
          </div>
        </section>

        {/* ---------- Enquiry ---------- */}
        <section className="svp-section svp-section--cta" id="svp-enquire">
          <SceneBackdrop type={page.slug} soft />
          <div className="svp-container svp-enquire">
            <div className="svp-enquire__copy" data-reveal="left">
              <span className="svp-chip svp-chip--light">Get in touch</span>
              <h2>{page.cta.title}</h2>
              <p>{page.cta.text}</p>
              <ul className="svp-enquire__contacts">
                <li>
                  <a href={CONTACT_INFO.phoneHref}>
                    <span aria-hidden="true">
                      <i className="fa-solid fa-phone" />
                    </span>
                    <span>
                      <small>Call us</small>
                      {CONTACT_INFO.phone}
                    </span>
                  </a>
                </li>
                <li>
                  <a href={whatsappLink(`Hello Fabritech, I am interested in ${page.name}.`)} target="_blank" rel="noreferrer">
                    <span aria-hidden="true">
                      <i className="fa-brands fa-whatsapp" />
                    </span>
                    <span>
                      <small>WhatsApp</small>
                      Chat with our team
                    </span>
                  </a>
                </li>
                <li>
                  <a href={CONTACT_INFO.mapsHref} target="_blank" rel="noreferrer">
                    <span aria-hidden="true">
                      <i className="fa-solid fa-location-dot" />
                    </span>
                    <span>
                      <small>Visit us</small>
                      {CONTACT_INFO.address}, Kigali
                    </span>
                  </a>
                </li>
              </ul>
            </div>
            <div data-reveal="right">
              <EnquiryForm key={page.slug} page={page} />
            </div>
          </div>
        </section>

        {/* ---------- Other services ---------- */}
        <section className="svp-section">
          <div className="svp-container">
            <Head chip="More from Fabritech" title="Explore our other" accent="services" />
            <ul className="svp-others">
              {others.map((item, index) => (
                <li key={item.slug} data-reveal="zoom" style={{ '--i': index }}>
                  <Link to={servicePath(item.slug)} className="svp-other" data-tilt="6">
                    <span className="svp-icon-chip" aria-hidden="true">
                      <i className={`fa-solid ${item.icon}`} />
                    </span>
                    <span className="svp-other__body">
                      <strong>{item.name}</strong>
                      <small>{item.summary}</small>
                    </span>
                    <i className="fa-solid fa-arrow-right svp-other__go" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>

      <HomeFooter />
      <CartDrawer open={showCart} onClose={() => setShowCart(false)} />
    </div>
  )
}

export default ServicePage
